import { useParams, Link } from 'react-router-dom';
import { articles as articleData } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Helmet from '@/pages/home/components/Helmet';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articleData.find((a) => a.slug === slug);
  const category = categories.find((c) => c.slug === article?.category);

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="min-h-screen pt-20 flex items-center justify-center">
          <div className="text-center">
            <h1 className="font-heading text-2xl text-foreground-950">文章未找到</h1>
            <Link
              to="/"
              className="mt-4 inline-flex items-center text-primary-500 hover:text-primary-600 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line mr-1"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articleData
    .filter((a) => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: '妈妈的朋友们',
    },
  };

  return (
    <>
      <ScrollToTop />
      <Helmet
        title={`${article.title} - 妈妈的朋友们`}
        description={article.excerpt}
        keywords={article.tags.join(',') + ',妈妈的朋友们,妈妈的故事'}
        canonicalUrl={`https://momfriends.cn/article/${slug}`}
        ogTitle={article.title}
        ogDescription={article.excerpt}
        ogType="article"
      />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <Navbar />
      <main className="min-h-screen pt-20">
        <article>
          <div className="bg-background-50">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                ...(category
                  ? [{ label: category.name, path: `/category/${category.slug}` }]
                  : []),
                { label: article.title },
              ]}
            />

            <div className="w-full px-4 md:px-6 py-8 md:py-12">
              <div className="max-w-3xl mx-auto">
                <div className="relative w-full h-[280px] md:h-[400px] rounded-lg overflow-hidden mb-8">
                  <img
                    src={article.coverImage}
                    alt={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>

                <h1 className="font-heading text-2xl md:text-3xl text-foreground-950 leading-snug">
                  {article.title}
                </h1>

                <div className="mt-4 flex flex-wrap items-center gap-3 md:gap-4 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img
                        src={article.authorAvatar}
                        alt={article.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <span>{article.author}</span>
                  </div>
                  <span>·</span>
                  <span>{article.date}</span>
                  <span>·</span>
                  <span>{article.readTime}</span>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-secondary-100 text-secondary-800 rounded-full text-xs font-label whitespace-nowrap"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                <div
                  className="mt-8 prose prose-lg max-w-none
                    prose-headings:font-heading prose-headings:text-foreground-950
                    prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-base prose-p:my-5
                    prose-strong:text-foreground-950"
                  dangerouslySetInnerHTML={{ __html: article.content }}
                ></div>

                <div className="mt-12 pt-8 border-t border-background-200/70">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full overflow-hidden">
                      <img
                        src={article.authorAvatar}
                        alt={article.author}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div>
                      <p className="font-label text-sm text-foreground-950 font-medium">
                        {article.author}
                      </p>
                      <p className="text-xs text-foreground-500">
                        妈妈的朋友们 · 作者
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {relatedArticles.length > 0 && (
            <div className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-3xl mx-auto">
                <h3 className="font-heading text-xl text-foreground-950 mb-6">
                  相关故事
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.slug}
                      to={`/article/${ra.slug}`}
                      className="group block bg-background-50 rounded-lg overflow-hidden hover:shadow-sm transition-all duration-300 cursor-pointer"
                    >
                      <div className="w-full h-36 overflow-hidden">
                        <img
                          src={ra.coverImage}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h4 className="font-heading text-sm text-foreground-950 group-hover:text-primary-500 transition-colors duration-200 line-clamp-2">
                          {ra.title}
                        </h4>
                        <p className="mt-1 text-xs text-foreground-500">{ra.date}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}